/* Sağlık — "site şu an ayakta mı, son bir günde nasıldı, biri saldırıyor mu".

   Üç kaynak, üç bölüm, üç ayrı istek (routes/adminHealth.ts):

     Şu an          canlı yoklama — dört hedef, milisaniye
     Son 24 saat    zamanlanmış işin kayıtları — her hedefe bir grafik
     Kenar          Cloudflare'in saydıkları — istek, hata, güvenlik

   İstekler paralel ve birbirinden bağımsız: Cloudflare jetonu yoksa kenar
   bölümü "yapılandırılmamış" der, yoklamalar yine görünür. Uyarılar en
   üstte, çünkü sekmenin asıl sorusu o. */

import { siteHealth } from '../api.ts';
import type { EdgeReport, HealthLive, Probe, ProbeTarget, TargetHistory } from '../api.ts';
import { barChart, lineChart } from '../chart.ts';
import { el, replace } from '../dom.ts';
import type { AppContext } from '../main.ts';
import { kpi, ranked } from './stats.ts';

const TARGETS: Record<ProbeTarget, { name: string; why: string }> = {
  site: { name: 'Site', why: 'Ana sayfa — Pages ve alan adı' },
  db: { name: 'Veritabanı', why: 'Neon — içerik ve oturumlar' },
  r2: { name: 'Görsel deposu', why: 'R2 gezinme kovası' },
  notfound: { name: '404 davranışı', why: 'Bilinmeyen yol gerçekten 404 mü' }
};

const ORDER: ProbeTarget[] = ['site', 'db', 'r2', 'notfound'];

/** Bağlama tablosunun Türkçesi (`/api/health` ile aynı anahtarlar). */
const BINDINGS: Record<string, { name: string; why: string }> = {
  database: { name: 'Veritabanı', why: 'İçerik ve iletişim kutusu' },
  photos: { name: 'Gezinme kovası', why: 'Herkese açık 900 px katmanı' },
  photosFull: { name: 'Büyütme kovası', why: 'İmzalı tam boy katman' },
  uploadSigning: { name: 'Yükleme imzası', why: 'R2 API jetonu — yükleme bununla çalışır' },
  publish: { name: 'Yayın tetiği', why: 'GitHub Actions derlemesini başlatır' },
  adminSessions: { name: 'Oturum deposu', why: 'Yönetici girişi' },
  accessLock: { name: 'Cloudflare Access', why: 'Panelin dış çemberi' },
  adminLock: { name: 'Paylaşılan giz', why: 'Access gelene kadarki geçici kilit' },
  turnstile: { name: 'Turnstile', why: 'İletişim formunun spam kalkanı' },
  email: { name: 'E-posta', why: 'Talep bildirimi ve alındı yanıtı' },
  analytics: { name: 'Cloudflare analitiği', why: 'Bu sekmenin kenar bölümü ve Trafik' }
};

/**
 * Eksikliği gerçekten sorun olan bağlamalar. `adminLock` ve `accessLock`
 * kapalıyken de doğru olabilir; hepsini kırmızı göstermek, gerçekten kırmızı
 * olanı görünmez yapardı.
 */
const REQUIRED = new Set(['database', 'photos', 'photosFull', 'uploadSigning', 'adminSessions']);

export async function renderHealth(host: HTMLElement, ctx: AppContext): Promise<void> {
  const alerts = el('div', { class: 'alerts' });
  const now = el('div', { class: 'tiles' }, el('p', { class: 'note', text: 'Yoklanıyor…' }));
  const history = el('div', { class: 'multiples' }, el('p', { class: 'note', text: 'Okunuyor…' }));
  const edge = el('div', {}, el('p', { class: 'note', text: 'Cloudflare okunuyor…' }));
  const bindings = el('div');
  const refresh = el('button', { type: 'button', text: 'Yenile', onclick: () => void renderHealth(host, ctx) });

  replace(
    host,
    el('div', { class: 'row' }, el('h1', { text: 'Sağlık', style: 'margin:0' }), el('span', { class: 'spacer' }), refresh),
    el('div', { style: 'height:14px' }),
    alerts,
    el('h2', { text: 'Şu an' }),
    now,
    el('h2', { text: 'Son 24 saat — yanıt süresi' }),
    el('p', {
      class: 'note',
      text: 'Worker her beş dakikada dört hedefi yokluyor. Kırmızı işaretler kesinti. Worker\'ın kendisi düşerse bu kayıt da durur — dışarıdan bir izleyici (UptimeRobot) önerilir.'
    }),
    history,
    el('h2', { text: 'Kenar ve güvenlik — son 24 saat' }),
    edge,
    el('h2', { text: 'Yapılandırma' }),
    bindings
  );

  const [live, past, report] = await Promise.allSettled([siteHealth.live(), siteHealth.history(24), siteHealth.edge()]);

  const problems: string[] = [];

  if (live.status === 'fulfilled') {
    replace(bindings, drawBindings(live.value.bindings));
    for (const p of live.value.probes.filter((x) => !x.ok)) problems.push(`${TARGETS[p.target].name}: ${p.note ?? 'cevap yok'}`);
  } else {
    replace(bindings, el('p', { class: 'err', text: 'Yapılandırma okunamadı.' }));
    ctx.fail(live.reason);
  }

  const pastTargets = past.status === 'fulfilled' ? past.value.targets : null;
  replace(now, ...drawTiles(live.status === 'fulfilled' ? live.value : null, pastTargets));

  if (pastTargets) {
    replace(history, ...drawHistory(pastTargets));
  } else {
    replace(history, el('p', { class: 'err', text: reasonText(past.status === 'rejected' ? past.reason : null, 'Geçmiş okunamadı.') }));
  }

  if (report.status === 'fulfilled') {
    replace(edge, drawEdge(report.value));
    problems.push(...report.value.alerts.map((a) => a.message));
  } else {
    replace(edge, el('p', { class: 'note', text: reasonText(report.reason, 'Cloudflare verisi okunamadı.') }));
  }

  replace(
    alerts,
    problems.length === 0
      ? el('p', { class: 'banner', 'data-kind': 'ok', text: '● Her şey yolunda — bütün yoklamalar geçti, uyarı yok.' })
      : el('div', { class: 'banner', 'data-kind': 'bad' }, ...problems.map((text) => el('p', { text: `✕ ${text}` })))
  );
}

function reasonText(reason: unknown, fallback: string): string {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}

/* ── Şu an ─────────────────────────────────────────────── */

function drawTiles(live: HealthLive | null, past: TargetHistory[] | null): HTMLElement[] {
  return ORDER.map((target) => {
    const probe: Probe | undefined = live?.probes.find((p) => p.target === target);
    const day = past?.find((p) => p.target === target);
    const state = !probe ? 'dim' : probe.ok ? 'ok' : 'bad';
    const label = !probe ? 'bilinmiyor' : probe.ok ? '● ayakta' : '✕ sorun';
    return el(
      'div',
      { class: 'tile' },
      el('p', { class: 'tile-name', text: TARGETS[target].name }),
      el('p', { class: 'tile-value', text: probe && probe.ms > 0 ? `${probe.ms} ms` : '—' }),
      el('p', { class: state, text: probe?.note ? `${label} · ${probe.note}` : label }),
      el('p', {
        class: 'dim',
        text: day && day.uptime !== null ? `24 saat: %${(day.uptime * 100).toFixed(1)} · p95 ${day.p95 ?? '—'} ms` : '24 saat: kayıt yok'
      })
    );
  });
}

/* ── Son 24 saat ───────────────────────────────────────── */

function drawHistory(targets: TargetHistory[]): HTMLElement[] {
  const to = Date.now();
  const from = to - 24 * 3_600_000;
  if (targets.every((t) => t.checks === 0)) {
    return [
      el('p', {
        class: 'note',
        text: 'Henüz kayıt yok. Zamanlanmış iş yalnızca üretimde çalışır; API dağıtıldıktan beş dakika sonra ilk satırlar görünür.'
      })
    ];
  }
  return ORDER.map((target) => {
    const own = targets.find((t) => t.target === target);
    const points = (own?.series ?? []).map(([at, ms, ok]) => ({ at, value: ms, ok }));
    return el(
      'section',
      { class: 'multiple' },
      el('p', { class: 'tile-name', text: TARGETS[target].name }),
      lineChart({ label: `${TARGETS[target].name} yanıt süresi, son 24 saat`, points, from, to, unit: 'ms', height: 110 })
    );
  });
}

/* ── Kenar ─────────────────────────────────────────────── */

function drawEdge(report: EdgeReport): HTMLElement {
  const total = report.hours.reduce((s, h) => s + h.requests, 0);
  const cached = report.hours.reduce((s, h) => s + h.cached, 0);
  const s5xx = report.hours.reduce((s, h) => s + h.s5xx, 0);
  const events = report.security.hourly.reduce((s, h) => s + h.events, 0);
  const pct = (part: number, whole: number) => (whole > 0 ? `%${((part / whole) * 100).toFixed(1)}` : '—');

  const kpis = el(
    'div',
    { class: 'tiles' },
    kpi('İstek', total.toLocaleString('tr-TR'), `haftalık ort. ${Math.round(report.baselinePerHour * 24).toLocaleString('tr-TR')}/gün`),
    kpi('Önbellekten', pct(cached, total), 'kenarda karşılanan'),
    kpi('Sunucu hatası', pct(s5xx, total), `${s5xx.toLocaleString('tr-TR')} istek (5xx)`),
    kpi('Güvenlik olayı', events.toLocaleString('tr-TR'), 'engelleme, challenge, kayıt')
  );

  const bars = report.hours.map((h) => ({
    at: Date.parse(h.hour),
    value: h.requests,
    detail: [
      `${h.requests.toLocaleString('tr-TR')} istek`,
      `4xx ${h.s4xx} · 5xx ${h.s5xx}`,
      `önbellek ${pct(h.cached, h.requests)}`
    ]
  }));

  const workerTotal = report.workers.reduce((s, w) => s + w.requests, 0);
  const workerErrors = report.workers.reduce((s, w) => s + w.errors, 0);
  const cpuP99 = Math.max(0, ...report.workers.map((w) => w.cpuP99));

  return el(
    'div',
    {},
    kpis,
    el('div', { style: 'height:16px' }),
    barChart({ label: 'Saat başına istek, son 24 saat', bars, unit: 'istek', height: 170 }),
    el(
      'p',
      { class: 'note' },
      workerTotal > 0
        ? `API (Worker): ${workerTotal.toLocaleString('tr-TR')} çağrı, ${workerErrors} hata, CPU p99 ${cpuP99.toFixed(1)} ms.`
        : 'API (Worker): son 24 saatte çağrı yok.'
    ),
    el(
      'div',
      { class: 'cols' },
      ranked('En çok istek atan IP', report.security.byIp, total),
      ranked('Ülke', report.security.byCountry, total),
      ranked('Yol', report.security.byPath, total),
      ranked('Güvenlik eylemi', report.security.byAction.map((a) => ({ ...a, key: a.key === '—' ? 'eylem yok' : a.key })), total)
    ),
    el('p', { class: 'dim', text: `Cloudflare verisi ${new Date(report.cachedAt).toLocaleTimeString('tr-TR')} itibarıyla; beş dakikada bir tazelenir.` })
  );
}

/* ── Yapılandırma ──────────────────────────────────────── */

function drawBindings(bindings: Record<string, boolean>): HTMLElement {
  const rows = Object.entries(bindings).map(([key, ok]) => {
    const meta = BINDINGS[key] ?? { name: key, why: '' };
    const required = REQUIRED.has(key);
    return el(
      'tr',
      {},
      el('td', { text: meta.name }),
      el('td', { class: 'dim', text: meta.why }),
      el('td', { class: ok ? 'ok' : required ? 'bad' : 'dim', text: ok ? 'bağlı' : required ? 'EKSİK' : 'kapalı' })
    );
  });
  return el(
    'table',
    {},
    el('thead', {}, el('tr', {}, el('th', { text: 'bileşen' }), el('th', { text: 'ne için' }), el('th', { text: 'durum' }))),
    el('tbody', {}, ...rows)
  );
}
